import { ReactElement, ReactNode } from 'react';
import { Button, Dropdown, Menu, Space } from 'antd';
import { Confirm } from 'components/Confirm/Confirm';
import { MoreOutlined } from '@ant-design/icons';
import { ButtonShape, ButtonType } from 'antd/lib/button';
import { SizeType } from 'antd/lib/config-provider/SizeContext';
import { Boolean } from "@methodset/commons-core-ts";
import { CoreUtils } from 'utils/CoreUtils';
import classNames from 'classnames';
import './MenuButton.less';

export type LabelFunction = (data: any) => string;
export type ConfirmFunction = (data: any) => string | undefined;
export type DisabledFunction = (data: any) => boolean;
export type HiddenFunction = (data: any) => boolean;
export type SelectCallback = (data?: any) => void;

export interface ItemSpec {
    label: string | LabelFunction,
    icon?: ReactNode,
    confirm?: string | ConfirmFunction,
    menu?: ReactElement,
    children?: ItemSpec[],
    disabled?: DisabledFunction | boolean,
    hidden?: HiddenFunction,
    onSelect?: SelectCallback
}

export type MenuButtonProps = {
    className?: string,
    dropClassName?: string,
    label?: string,
    items: ItemSpec[],
    type?: ButtonType,
    shape?: ButtonShape,
    icon?: ReactNode,
    size?: SizeType,
    color?: string,
    data?: any,
    index?: number
} & typeof defaultProps;

const defaultProps = {
    icon: <MoreOutlined />
}

/**
 * A button with a menu of items in a dropdown when the button is clicked.
 */
export const MenuButton = (props: MenuButtonProps): ReactElement => {

    const isDisabled = (item: ItemSpec): boolean => {
        if (CoreUtils.isEmpty(item.disabled)) {
            return false;
        }
        return Boolean.isBoolean(item.disabled) ? item.disabled : item.disabled!(props.data);
    }

    const buildMenu = (items: ItemSpec[], level: number): ReactElement => {
        return (
            <>
                {items.map((item, index) => {
                    const label = typeof item.label === "function" ? item.label(props.data) : item.label;
                    const confirm = typeof item.confirm === "function" ? item.confirm(props.data) : item.confirm;
                    if (confirm && item.onSelect && (!item.hidden || !item.hidden(props.data))) {
                        return (
                            <Menu.Item
                                key={`${level}-${index}`}
                                disabled={isDisabled(item)}
                            >
                                <Confirm
                                    placement="left"
                                    message={confirm}
                                    onConfirm={() => item.onSelect!(props.data)}
                                >
                                    <Space>
                                        <span>{item.icon}</span>
                                        <span>{label}</span>
                                    </Space>
                                </Confirm>
                            </Menu.Item>
                        )
                    } else if (item.menu && (!item.hidden || !item.hidden(props.data))) {
                        return (
                            <Menu.SubMenu
                                key={`${level}-${index}`}
                                icon={item.icon}
                                title={label}
                                disabled={isDisabled(item)}
                            >
                                {item.menu}
                            </Menu.SubMenu>
                        )
                    } else if (item.children && (!item.hidden || !item.hidden(props.data))) {
                        return (
                            <Menu.SubMenu
                                key={`${level}-${index}`}
                                icon={item.icon}
                                title={label}
                                disabled={isDisabled(item)}
                            >
                                {buildMenu(item.children, level + 1)}
                            </Menu.SubMenu>
                        )
                    } else if (item.onSelect && (!item.hidden || !item.hidden(props.data))) {
                        return (
                            <Menu.Item
                                key={`${level}-${index}`}
                                disabled={isDisabled(item)}
                                onClick={() => item.onSelect!(props.data)}
                            >
                                <Space>
                                    <span>{item.icon}</span>
                                    <span>{label}</span>
                                </Space>
                            </Menu.Item>
                        )
                    } else {
                        return null;
                    }
                })}
            </>
        )
    }

    const menu = (
        <Menu>
            {buildMenu(props.items, 0)}
        </Menu>
    )

    return (
        <div className={classNames('x-menubutton', props.className)}>
            <Dropdown
                overlay={menu}
                trigger={['click']}
            >
                <Button
                    className={props.dropClassName}
                    type={props.type}
                    shape={props.shape}
                    icon={!props.label ? props.icon : undefined}
                    size={props.size}
                    style={{color: props.color}}
                >
                    {props.label &&
                        <Space>
                            <span>{props.label}</span>
                            <span>{props.icon}</span>
                        </Space>
                    }
                </Button>
            </Dropdown>
        </div>
    )
}

MenuButton.defaultProps = defaultProps;
